// ============================================================================
// EcgResultView.tsx — результат разбора ЭКГ (шаг А): статус, качество снимка,
// предпросмотр сигнала, измерения, вероятностные находки и блок «Почему»
// (доказательства из ответа сервиса — см. EcgExplainPanel). Только отображение
// ответа сервиса; сохранение разбора — в EcgAnalysisPage (шаг Б).
// ============================================================================

import type { EcgAnalysis } from '@/pages/EcgAnalysisPage';
import { EcgExplainPanel } from '@/components/ecg/EcgExplainPanel';
import { EcgFindingDetails, EcgMiHypStatusBanner } from '@/components/ecg/EcgFindingDetails';
import { ecgQualityVerdictLabel } from '@/lib/ecgLabels';

const STATUS_LABELS: Record<string, string> = {
    ok: 'Разобрано',
    retake: 'Нужен повторный снимок',
    failed: 'Разбор не выполнен',
};

function statusTone(status: string): string {
    if (status === 'ok') return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700';
    if (status === 'retake') return 'border-amber-500/50 bg-amber-500/10 text-amber-700';
    return 'border-destructive/40 bg-destructive/10 text-destructive';
}

function fmtNumber(value: number | null, digits = 1): string {
    if (value === null) return '—';
    return Number.isInteger(value) ? String(value) : value.toFixed(digits);
}

function fmtCalibration(value: boolean | null): string {
    if (value === null) return '—';
    return value ? 'найдена' : 'не найдена';
}

function fmtProbability(value: number | null): string {
    if (value === null) return '—';
    return `${Math.round(value * 100)}%`;
}

function Metric({ label, value, unit }: { label: string; value: string; unit?: string }) {
    return (
        <div className="border rounded-md px-3 py-2 bg-card">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-sm font-medium mt-0.5">
                {value}
                {unit && value !== '—' && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
            </div>
        </div>
    );
}

export function EcgResultView({ result }: { result: EcgAnalysis }) {
    return (
        <div className="space-y-4">
            <section className="border rounded-lg p-3 bg-card space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-xs font-medium border rounded-md px-2 py-0.5 ${statusTone(result.status)}`}>
                        {STATUS_LABELS[result.status] || result.status || 'Неизвестно'}
                    </span>
                    {result.pipelineVersion && (
                        <span className="text-[11px] text-muted-foreground">версия: {result.pipelineVersion}</span>
                    )}
                </div>
                {result.reason && <div className="text-sm">{result.reason}</div>}
            </section>

            <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Качество снимка</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Metric label="Вердикт" value={ecgQualityVerdictLabel(result.quality.verdict)} />
                    <Metric label="Отведений" value={fmtNumber(result.quality.leadsExtracted, 0)} />
                    <Metric label="px/мм" value={fmtNumber(result.quality.pxPerMm, 2)} />
                    <Metric label="Калибровка" value={fmtCalibration(result.quality.calibrationFound)} />
                </div>
            </section>

            {result.previewPngBase64 && (
                <section>
                    <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Предпросмотр сигнала</h3>
                    <div className="border rounded-lg p-2 bg-card overflow-auto">
                        <img
                            src={`data:image/png;base64,${result.previewPngBase64}`}
                            alt="Предпросмотр сигнала ЭКГ"
                            className="max-w-full h-auto"
                        />
                    </div>
                </section>
            )}

            <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Измерения</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Metric label="ЧСС" value={fmtNumber(result.measurements.hrBpm, 0)} unit="уд/мин" />
                    <Metric label="PR" value={fmtNumber(result.measurements.prMs, 0)} unit="мс" />
                    <Metric label="QRS" value={fmtNumber(result.measurements.qrsMs, 0)} unit="мс" />
                    <Metric label="QT" value={fmtNumber(result.measurements.qtMs, 0)} unit="мс" />
                    <Metric label="QTc" value={fmtNumber(result.measurements.qtcMs, 0)} unit="мс" />
                    <Metric label="Электрическая ось" value={fmtNumber(result.measurements.axisDeg, 0)} unit="°" />
                </div>
            </section>

            <section className="space-y-2">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Вероятностные находки</h3>
                {result.miHyp && (
                    <EcgMiHypStatusBanner status={result.miHyp.status} reason={result.miHyp.reason} />
                )}
                {result.findings.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                        Находок нет. Причины — в блоке «Почему»: сервис мог не передать вероятности и пороги.
                    </div>
                ) : (
                    <ul className="border rounded-lg divide-y bg-card">
                        {result.findings.map((finding, index) => (
                            <li key={`${finding.code}-${index}`} className="px-3 py-2 text-sm">
                                <div className="flex items-center justify-between gap-2">
                                    <span>{finding.code}</span>
                                    <span className="text-xs text-muted-foreground">{fmtProbability(finding.probability)}</span>
                                </div>
                                <EcgFindingDetails finding={finding} />
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <EcgExplainPanel result={result} />
        </div>
    );
}
